import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import './index.css';
import FormulatorHeader from './FormulatorHeader.png';
import { auth, db } from './firebase';
import { collection, query, where, onSnapshot, deleteDoc, doc, addDoc, setDoc } from 'firebase/firestore';
import { signOut } from 'firebase/auth';

function FormulaLibrary() {
  const navigate = useNavigate();
  const [formulas, setFormulas] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState({
    tintId: '',
    colorName: '',
    woodSpecies: '',
    customer: '',
    date: '',
    stainType: '' // Replace formulator
  });
  const [selectedFormulas, setSelectedFormulas] = useState(new Set());
  const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);
  const [formulaToDelete, setFormulaToDelete] = useState(null);
  const [isImagePopupOpen, setIsImagePopupOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const fileInputRef = useRef(null);
  const [sortConfig, setSortConfig] = useState({ key: 'tintId', direction: 'asc' });
  const [isDeletePhotoConfirmOpen, setIsDeletePhotoConfirmOpen] = useState(false);
  const [photoToDelete, setPhotoToDelete] = useState(null);
  const [viewMode, setViewMode] = useState('list');
  const [selectedFormulaForDetails, setSelectedFormulaForDetails] = useState(null);
  const [isDetailsPopupOpen, setIsDetailsPopupOpen] = useState(false);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackName, setFeedbackName] = useState('');

  useEffect(() => {
    if (!auth.currentUser) {
      console.log("No user authenticated, clearing formulas");
      setFormulas([]);
      setIsLoading(false);
      return;
    }

    console.log("Setting up Firestore listener for user:", auth.currentUser.uid);
    const q = query(collection(db, 'formulas'), where('userId', '==', auth.currentUser.uid));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const fetchedFormulas = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      console.log("Fetched formulas:", fetchedFormulas);
      setFormulas(fetchedFormulas);
      setIsLoading(false);
    }, (error) => {
      console.error("Error fetching formulas:", error);
      alert("Failed to load formulas: " + error.message);
      setIsLoading(false);
    });

    return () => {
      console.log("Cleaning up Firestore listener");
      unsubscribe();
    };
  }, []);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (tintId) => {
    setSelectedFormulas(prev => {
      const newSet = new Set(prev);
      if (newSet.has(tintId)) {
        newSet.delete(tintId);
      } else {
        newSet.add(tintId);
      }
      return newSet;
    });
  };

  const handleSelectAllChange = (e) => {
    const isChecked = e.target.checked;
    setSelectedFormulas(prev => {
      const newSet = new Set(prev);
      if (isChecked) {
        filteredFormulas.forEach(formula => newSet.add(formula.tintId));
      } else {
        filteredFormulas.forEach(formula => newSet.delete(formula.tintId));
      }
      return newSet;
    });
  };

  const deleteFormula = async (tintId) => {
    const formula = formulas.find(f => f.tintId === tintId);
    if (!formula || !formula.id) return;
    console.log('Attempting to delete formula:', { formulaId: formula.id, formulaUserId: formula.userId, currentUserUid: auth.currentUser?.uid });
    if (formula.userId !== auth.currentUser?.uid) {
      console.error('UserId mismatch - cannot delete');
      alert('Cannot delete: This formula belongs to a different user.');
      return;
    }
    try {
      await deleteDoc(doc(db, 'formulas', formula.id));
      setSelectedFormulas(prev => {
        const newSet = new Set(prev);
        newSet.delete(tintId);
        return newSet;
      });
      setIsDeletePopupOpen(false);
      setFormulaToDelete(null);
      console.log(`Formula ${tintId} deleted from Firestore`);
    } catch (error) {
      console.error("Error deleting formula:", error);
      alert("Failed to delete formula: " + error.message);
    }
  };

  const confirmDelete = (tintId) => {
    setFormulaToDelete(tintId);
    setIsDeletePopupOpen(true);
  };

  const editFormula = (formula) => {
    navigate('/formulator', { 
      state: { 
        formulaToEdit: formula, 
        initialEditMode: false,
        fromReTint: true
      } 
    });
  };

  const startNewFormula = async (formula) => {
    navigate('/formulator', { 
      state: {
        formulaToStart: {
          formulationVehicleStart: formula.formulationVehicleStart,
          binderPercentage: formula.binderPercentage,
          rowsData: formula.rowsData
        },
        initialEditMode: true
      }
    });
  };

  const handleNewFormula = () => {
    navigate('/formulator');
  };

  const handleClearFilters = () => {
    setFilters({
      tintId: '',
      colorName: '',
      woodSpecies: '',
      customer: '',
      date: '',
      stainType: '' // Replace formulator
    });
    setSelectedFormulas(new Set());
  };

  const openImagePopup = (image) => {
    setSelectedImage(image);
    setIsImagePopupOpen(true);
  };

  const exportFormulasToJSON = () => {
    if (selectedFormulas.size === 0) {
      alert('Please select at least one formula to export.');
      return;
    }

    const formulasToExport = formulas.filter(f => selectedFormulas.has(f.tintId));
    const json = JSON.stringify(formulasToExport, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'formulas_export.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const importFormulas = async (event) => {
    const file = event.target.files[0];
    if (!file || !auth.currentUser) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const importedFormulas = JSON.parse(e.target.result);
        if (!Array.isArray(importedFormulas)) {
          alert('Invalid file format. Please upload a valid JSON array of formulas.');
          return;
        }

        const existingTintIds = new Set(formulas.map(f => f.tintId));
        const promises = importedFormulas.map(async (formula) => {
          const newFormula = {
            ...formula,
            userId: auth.currentUser.uid,
            createdAt: new Date().toISOString(),
            importedFrom: formula.userId
          };
          if (!existingTintIds.has(newFormula.tintId)) {
            const docRef = await addDoc(collection(db, 'formulas'), newFormula);
            return { ...newFormula, id: docRef.id };
          }
          return null;
        });

        const newFormulas = (await Promise.all(promises)).filter(f => f !== null);
        alert(`Successfully imported ${newFormulas.length} new formulas. Duplicates skipped.`);
      } catch (error) {
        alert('Error importing formulas: ' + error.message);
      }
    };
    reader.readAsText(file);
    event.target.value = null;
  };

  const triggerImport = () => {
    fileInputRef.current.click();
  };

  const exportFormulasToPDF = () => {
    if (selectedFormulas.size === 0) {
      alert('Please select at least one formula to export to PDF.');
      return;
    }

    if (!formulas.every(f => f.rowsData && Array.isArray(f.rowsData))) {
      alert('Invalid formula data. Please check your formulas.');
      return;
    }

    const defaultFileName = 'Selected_Formulas.pdf';
    const userFileName = window.prompt('Enter the filename for your PDF:', defaultFileName);
    if (!userFileName) return;

    const fileName = userFileName.endsWith('.pdf') ? userFileName : `${userFileName}.pdf`;

    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4'
    });

    const selectedFormulasData = formulas.filter(f => selectedFormulas.has(f.tintId));

    selectedFormulasData.forEach((formula, index) => {
      if (index > 0) {
        doc.addPage();
      }

      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text(`Formula: ${formula.tintId} - ${formula.colorName || 'Unnamed'}`, 10, 10);
      doc.setFontSize(11);
      doc.setFont("helvetica", "normal");
      doc.text(`Wood Species: ${formula.woodSpecies || 'N/A'} | Customer: ${formula.customer || 'N/A'}`, 10, 20);
      doc.text(`Date: ${formula.date} | Stain Type: ${formula.stainType || 'N/A'}`, 10, 30);

      const headers = ['Ingredient', 'Shot 1', 'Shot 2', 'Shot 3', 'Shot 4', 'Total', 'Percentage', 'Custom Size', 'Standard Sizes'];
      const columnWidths = [70, 25, 25, 25, 25, 25, 25, 27, 30];
      let y = 38;

      doc.setFontSize(10.5);
      doc.setFont("helvetica", "bold");
      headers.forEach((header, idx) => {
        const x = 10 + columnWidths.slice(0, idx).reduce((a, b) => a + b, 0);
        doc.setFillColor(224, 224, 224);
        doc.rect(x, y, columnWidths[idx], 10, 'F');
        doc.setTextColor(0, 0, 0);
        doc.text(header, x + 2, y + 7);
      });
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      y += 10;

      const sections = [
        { title: "Vehicles (Solvents)", sectionClass: "vehicle", rows: formula.rowsData.slice(0, 2) },
        { title: "Binders", sectionClass: "binder", rows: formula.rowsData.slice(2, 4) },
        { title: "Colorants", sectionClass: "colorant", rows: formula.rowsData.slice(4) }
      ];

      const grandTotal = formula.rowsData.reduce((sum, row) => {
        const rowTotal = row.hits.reduce((s, hit) => s + (parseFloat(hit) || 0), 0);
        return sum + rowTotal;
      }, 0).toFixed(1);
      const standardSizesMap = {
        "Quart": 800,
        "Gallon": 3500,
        "5 Gallon": 17500
      };
      const desiredTotal = standardSizesMap[formula.standardSize] || 0;
      const scaleFactor = grandTotal > 0 ? desiredTotal / parseFloat(grandTotal) : 0;
      const standardValues = formula.rowsData.map(row => {
        const rowTotal = row.hits.reduce((s, hit) => s + (parseFloat(hit) || 0), 0);
        return desiredTotal > 0 ? (rowTotal * scaleFactor).toFixed(1) : '';
      });

      const vehicle1Amount = formula.rowsData[0]?.hits.reduce((s, hit) => s + (parseFloat(hit) || 0), 0) || 0;
      const customVehicle1Value = parseFloat(formula.customVehicle1) || 0;
      const customScaleFactor = vehicle1Amount > 0 ? customVehicle1Value / vehicle1Amount : 0;
      const customValues = formula.rowsData.map(row => {
        const rowTotal = row.hits.reduce((s, hit) => s + (parseFloat(hit) || 0), 0);
        return customVehicle1Value > 0 ? (rowTotal * customScaleFactor).toFixed(1) : '';
      });

      sections.forEach(section => {
        const sectionRow = [section.title, '', '', '', '', '', '', '', ''];
        sectionRow.forEach((cell, idx) => {
          const x = 10 + columnWidths.slice(0, idx).reduce((a, b) => a + b, 0);
          doc.setFillColor(idx === 0 ? 224 : 255, idx === 0 ? 224 : 255, idx === 0 ? 224 : 255);
          doc.rect(x, y, columnWidths[idx], 10, 'F');
          doc.setTextColor(0, 0, 0);
          doc.text(cell, x + 2, y + 7, { maxWidth: columnWidths[idx] - 4 });
        });
        y += 10;

        section.rows.forEach((row, rowIndex) => {
          const globalRowIndex = sections.slice(0, sections.indexOf(section)).reduce((sum, s) => sum + s.rows.length, 0) + rowIndex;
          const rowTotal = row.hits.reduce((sum, hit) => sum + (parseFloat(hit) || 0), 0).toFixed(1);
          const rowData = [
            row.selectedItem || row.label,
            row.hits[0] ? `${row.hits[0]}g` : '',
            row.hits[1] ? `${row.hits[1]}g` : '',
            row.hits[2] ? `${row.hits[2]}g` : '',
            row.hits[3] ? `${row.hits[3]}g` : '',
            rowTotal ? `${rowTotal}g` : '',
            grandTotal > 0 ? `${((parseFloat(rowTotal) / parseFloat(grandTotal)) * 100).toFixed(1)}%` : '',
            customValues[globalRowIndex] ? `${customValues[globalRowIndex]}g` : '',
            standardValues[globalRowIndex] ? `${standardValues[globalRowIndex]}g` : ''
          ];

          rowData.forEach((cell, idx) => {
            const x = 10 + columnWidths.slice(0, idx).reduce((a, b) => a + b, 0);
            doc.setFillColor(255, 255, 255);
            doc.rect(x, y, columnWidths[idx], 10, 'F');
            doc.setTextColor(0, 0, 0);
            doc.text(cell, x + 2, y + 7, { maxWidth: columnWidths[idx] - 4 });
          });
          y += 10;
        });
      });

      doc.setFillColor(200, 200, 200);
      doc.rect(10, y, 277, 10, 'F');
      doc.setTextColor(0, 0, 0);
      doc.text('Total', 10, y + 7);
      doc.text(grandTotal > 0 ? `${grandTotal}g` : '', 10 + columnWidths.slice(0, 5).reduce((a, b) => a + b, 0) + 2, y + 7);
    });

    doc.save(fileName);
  };

  const handleSort = (key) => {
    setSortConfig(prev => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      } else {
        return { key, direction: 'asc' };
      }
    });
  };

  const filteredFormulas = formulas
    .filter(formula => formula.tintId && typeof formula.tintId === 'string')
    .filter(formula =>
      String(formula.tintId).includes(filters.tintId) &&
      (formula.colorName || '').toLowerCase().includes(filters.colorName.toLowerCase()) &&
      (formula.woodSpecies || '').toLowerCase().includes(filters.woodSpecies.toLowerCase()) &&
      (formula.customer || '').toLowerCase().includes(filters.customer.toLowerCase()) &&
      (formula.date || '').includes(filters.date) &&
      (formula.stainType || '').toLowerCase().includes(filters.stainType.toLowerCase())
    );

  const sortedFormulas = [...filteredFormulas].sort((a, b) => {
    const { key, direction } = sortConfig;
    let aValue = a[key] || '';
    let bValue = b[key] || '';
    // For date, compare as date
    if (key === 'date') {
      aValue = new Date(aValue);
      bValue = new Date(bValue);
      if (aValue < bValue) return direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return direction === 'asc' ? 1 : -1;
      return 0;
    }
    // For string, compare case-insensitive
    aValue = String(aValue).toLowerCase();
    bValue = String(bValue).toLowerCase();
    if (aValue < bValue) return direction === 'asc' ? -1 : 1;
    if (aValue > bValue) return direction === 'asc' ? 1 : -1;
    return 0;
  });

  const allFilteredSelected = filteredFormulas.length > 0 && 
    filteredFormulas.every(formula => selectedFormulas.has(formula.tintId));

  const handleDeletePhoto = async () => {
    if (!photoToDelete) return;
    const formula = formulas.find(f => f.uploadedImage === photoToDelete);
    if (!formula || !formula.id) return;
    try {
      await setDoc(doc(db, 'formulas', formula.id), { uploadedImage: null }, { merge: true });
      setFormulas(prev => prev.map(f => f.id === formula.id ? { ...f, uploadedImage: null } : f));
      setIsImagePopupOpen(false);
      setIsDeletePhotoConfirmOpen(false);
      setPhotoToDelete(null);
    } catch (error) {
      alert('Failed to delete photo: ' + error.message);
    }
  };

  const handleSendFeedback = async () => {
    if (!feedbackText.trim()) return alert('Please enter feedback.');
    if (!feedbackName.trim()) return alert('Please enter your name.');
    try {
      await addDoc(collection(db, 'feedback'), {
        userId: auth.currentUser?.uid || 'anonymous',
        name: feedbackName.trim(),
        timestamp: new Date().toISOString(),
        notes: feedbackText
      });
      alert('Feedback sent! Thank you.');
      setIsFeedbackOpen(false);
      setFeedbackText('');
      setFeedbackName('');
    } catch (error) {
      alert('Failed to send feedback: ' + error.message);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      alert('Failed to log out: ' + error.message);
    }
  };

  return (
    <div style={{ padding: '20px' }}>
      <img src={FormulatorHeader} alt="Formulator Header" className="header-logo" />
      <div className="action-container" style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        marginBottom: '20px', 
        backgroundColor: '#373636', 
        padding: '15px', 
        borderRadius: '8px', 
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        width: '90%',
        marginLeft: 'auto',
        marginRight: 'auto'
      }}>
        <div className="left-actions" style={{ display: 'flex', gap: '10px' }}>
          <button className="new-formula-btn" onClick={handleNewFormula}>New Formula</button>
          <button
            className="toggle-view-btn"
            style={{ backgroundColor: 'Purple', color: '#fff', border: 'none' }}
            onClick={() => setViewMode(viewMode === 'list' ? 'thumbnail' : 'list')}
          >
            {viewMode === 'list' ? 'Thumbnail View' : 'List View'}
          </button>
        </div>
        {viewMode === 'list' && (
        <div className="right-actions" style={{ display: 'flex', gap: '10px' }}>
          <button className="import-btn" onClick={triggerImport}>Import</button>
          <button className="export-btn" onClick={exportFormulasToJSON}>Export</button>
          <button className="export-pdf-btn" onClick={exportFormulasToPDF}>Export to PDF</button>
          <button onClick={() => setIsFeedbackOpen(true)} className="feedback-btn">Developer Feedback</button>
          <button className="logout-btn" onClick={handleLogout}>Logout</button>
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            accept=".json"
            onChange={importFormulas}
          />
        </div>
        )}
      </div>
      {isLoading ? (
        <div>Loading formulas...</div>
      ) : (
        <>
          {viewMode === 'list' ? (
        <table className="library-table" style={{ 
          width: '90%', 
          margin: '0 auto', 
          padding: '0 15px', 
          boxSizing: 'border-box'
        }}>
          <thead>
            <tr>
              <th style={{ width: '40px' }}></th>
                  <th style={{ width: '120px' }}>Tint ID</th>
                  <th style={{ width: '400px' }}>Color Name</th>
                  <th style={{ width: '10%' }}>Wood Species</th>
                  <th style={{ width: '10%' }}>Customer</th>
                  <th style={{ width: '10%' }}>Date</th>
                  <th style={{ width: '10%', cursor: 'pointer' }} onClick={() => handleSort('stainType')}>Stain Type</th>
              <th style={{ width: '150px' }}></th>
              <th style={{ width: '130px' }}></th>
              <th style={{ width: '130px' }}></th>
              <th style={{ width: '130px' }}></th>
            </tr>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={allFilteredSelected}
                  onChange={handleSelectAllChange}
                />
              </th>
              <th><input type="text" name="tintId" value={filters.tintId} onChange={handleFilterChange} style={{ width: '100%' }} /></th>
              <th><input type="text" name="colorName" value={filters.colorName} onChange={handleFilterChange} style={{ width: '100%' }} /></th>
              <th><input type="text" name="woodSpecies" value={filters.woodSpecies} onChange={handleFilterChange} style={{ width: '100%' }} /></th>
              <th><input type="text" name="customer" value={filters.customer} onChange={handleFilterChange} style={{ width: '100%' }} /></th>
              <th><input type="text" name="date" value={filters.date} onChange={handleFilterChange} style={{ width: '100%' }} /></th>
                  <th><input type="text" name="stainType" value={filters.stainType} onChange={handleFilterChange} style={{ width: '100%' }} /></th>
              <th>
                <button className="clear-filters-btn" onClick={handleClearFilters}>Clear Filters</button>
              </th>
              <th></th>
              <th></th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {sortedFormulas.length === 0 ? (
              <tr>
                <td colSpan="11">No formulas found.</td>
              </tr>
            ) : (
              sortedFormulas.map((formula) => (
                <tr key={formula.tintId}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedFormulas.has(formula.tintId)}
                      onChange={() => handleCheckboxChange(formula.tintId)}
                    />
                  </td>
                  <td>{formula.tintId}</td>
                  <td>{formula.colorName || 'N/A'}</td>
                  <td>{formula.woodSpecies || 'N/A'}</td>
                  <td>{formula.customer || 'N/A'}</td>
                  <td>{formula.date || 'N/A'}</td>
                      <td>{formula.stainType || 'N/A'}</td>
                  <td>
                    {formula.uploadedImage ? (
                      <img
                        src={formula.uploadedImage}
                        alt="Thumbnail"
                        style={{ width: '50px', height: '50px', objectFit: 'cover', cursor: 'pointer' }}
                        onClick={() => openImagePopup(formula.uploadedImage)}
                      />
                    ) : (
                      'No Image'
                    )}
                  </td>
                  <td>
                    <button style={{ width: '100%', padding: '5px 0' }} onClick={() => editFormula(formula)}>Re-Tint</button>
                  </td>
                  <td>
                    <button style={{ width: '100%', padding: '5px 0' }} onClick={() => startNewFormula(formula)}>Formula Start</button>
                  </td>
                  <td>
                    <button
                      className="delete-btn"
                      style={{ width: '100%', padding: '5px 0' }}
                      onClick={() => confirmDelete(formula.tintId)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
          ) : (
            <>
              <div className="filters-container" style={{ display: 'flex', gap: '10px', marginBottom: '30px', width: '90%', margin: '0 auto' }}>
                <input type="text" name="tintId" value={filters.tintId} onChange={handleFilterChange} placeholder="Tint ID" />
                <input type="text" name="colorName" value={filters.colorName} onChange={handleFilterChange} placeholder="Color Name" />
                <input type="text" name="woodSpecies" value={filters.woodSpecies} onChange={handleFilterChange} placeholder="Wood Species" />
                <input type="text" name="customer" value={filters.customer} onChange={handleFilterChange} placeholder="Customer" />
                <input type="text" name="date" value={filters.date} onChange={handleFilterChange} placeholder="Date" />
                <input type="text" name="stainType" value={filters.stainType} onChange={handleFilterChange} placeholder="Stain Type" />
                <button className="clear-filters-btn" onClick={handleClearFilters}>Clear Filters</button>
              </div>
              <div className="thumbnail-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '20px', width: '90%', margin: '0 auto', paddingTop: '20px' }}>
                {sortedFormulas.length === 0 ? (
                  <p>No formulas found.</p>
                ) : (
                  sortedFormulas.map((formula) => (
                    <div key={formula.tintId} className="thumbnail-card" style={{ border: '1px solid #ccc', borderRadius: '8px', padding: '10px', textAlign: 'center', cursor: 'pointer' }} onClick={() => {
                      setSelectedFormulaForDetails(formula);
                      setIsDetailsPopupOpen(true);
                    }}>
                      <input
                        type="checkbox"
                        checked={selectedFormulas.has(formula.tintId)}
                        onChange={(e) => {
                          e.stopPropagation(); // Prevent popup on checkbox click
                          handleCheckboxChange(formula.tintId);
                        }}
                        style={{ position: 'absolute', top: '10px', left: '10px' }}
                      />
                      {formula.uploadedImage ? (
                        <img src={formula.uploadedImage} alt={formula.colorName} style={{ width: '100%', height: '150px', objectFit: 'cover', borderRadius: '4px' }} />
                      ) : (
                        <div style={{ width: '100%', height: '150px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#eee', borderRadius: '4px' }}>No Image</div>
                      )}
                      <p style={{ marginTop: '10px', fontWeight: 'bold', color: '#fff' }}>{formula.colorName || 'N/A'}</p>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </>
      )}
      {isDeletePopupOpen && (
        <div className="delete-popup">
          <div className="delete-popup-content">
            <h2>Are you sure?</h2>
            <p>Do you really want to delete this formula? This action cannot be undone.</p>
            <button
              className="confirm-delete-btn"
              style={{ marginRight: '10px' }}
              onClick={() => deleteFormula(formulaToDelete)}
            >
              Yes, Delete
            </button>
            <button
              className="cancel-btn"
              onClick={() => setIsDeletePopupOpen(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      {isImagePopupOpen && (
        <div className="overlay" />
      )}
      {isImagePopupOpen && (
        <div className="image-popup">
          <div className="image-popup-content">
            <h2>Image Preview</h2>
            <img
              src={selectedImage}
              alt=""
              style={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: '8px', margin: '10px 0' }}
            />
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', marginTop: '10px' }}>
              <button onClick={() => setIsImagePopupOpen(false)}>Close</button>
              {(() => {
                const formula = formulas.find(f => f.uploadedImage === selectedImage);
                if (formula && formula.uploadedImage && formula.id) {
                  return (
                    <button style={{ backgroundColor: '#dc3545' }} onClick={() => { setIsDeletePhotoConfirmOpen(true); setPhotoToDelete(selectedImage); }}>Delete Photo</button>
                  );
                }
                return null;
              })()}
            </div>
          </div>
        </div>
      )}
      {isDeletePhotoConfirmOpen && (
        <div className="overlay" />
      )}
      {isDeletePhotoConfirmOpen && (
        <div className="delete-popup">
          <div className="delete-popup-content">
            <h2>Are you sure?</h2>
            <p>Do you really want to delete this photo? This action cannot be undone.</p>
            <button className="confirm-delete-btn" style={{ marginRight: '10px' }} onClick={handleDeletePhoto}>Yes, Delete</button>
            <button className="cancel-btn" onClick={() => { setIsDeletePhotoConfirmOpen(false); setPhotoToDelete(null); }}>Cancel</button>
          </div>
        </div>
      )}
      {isDetailsPopupOpen && selectedFormulaForDetails && (
        <>
          <div className="overlay" />
          <div className="details-popup" style={{ 
            position: 'fixed', 
            top: '50%', 
            left: '50%', 
            transform: 'translate(-50%, -50%)', 
            backgroundColor: 'rgba(74, 74, 74, 0.95)', 
            padding: '30px', 
            borderRadius: '12px', 
            zIndex: 1000, 
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.4)', 
            minWidth: '400px', 
            color: '#fff', 
            backdropFilter: 'blur(4px)', 
            WebkitBackdropFilter: 'blur(4px)' 
          }}>
            <div className="details-popup-content" style={{ display: 'flex', flexDirection: 'column', gap: '20px', alignItems: 'center', textAlign: 'center' }}>
              <h2>{selectedFormulaForDetails.colorName || 'N/A'}</h2>
              <p><strong>Tint ID:</strong> {selectedFormulaForDetails.tintId}</p>
              <p><strong>Wood Species:</strong> {selectedFormulaForDetails.woodSpecies || 'N/A'}</p>
              <p><strong>Customer:</strong> {selectedFormulaForDetails.customer || 'N/A'}</p>
              <p><strong>Date:</strong> {selectedFormulaForDetails.date || 'N/A'}</p>
              <p><strong>Stain Type:</strong> {selectedFormulaForDetails.stainType || 'N/A'}</p>
              {selectedFormulaForDetails.uploadedImage && (
                <img src={selectedFormulaForDetails.uploadedImage} alt={selectedFormulaForDetails.colorName ? selectedFormulaForDetails.colorName : "Formula"} style={{ maxWidth: '100%', maxHeight: '300px', margin: '10px 0', borderRadius: '8px' }} />
              )}
              <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                <button onClick={() => editFormula(selectedFormulaForDetails)}>Re-Tint</button>
                <button onClick={() => startNewFormula(selectedFormulaForDetails)}>Formula Start</button>
                <button className="delete-btn" onClick={() => confirmDelete(selectedFormulaForDetails.tintId)}>Delete</button>
              </div>
              <button onClick={() => setIsDetailsPopupOpen(false)} style={{ marginTop: '10px' }}>Close</button>
            </div>
          </div>
        </>
      )}
      {isFeedbackOpen && (
        <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', backgroundColor: '#373636', padding: '20px', borderRadius: '8px', zIndex: 1000, color: '#fff', width: '400px' }}>
          <h2>Developer Feedback</h2>
          <input
            type="text"
            value={feedbackName}
            onChange={(e) => setFeedbackName(e.target.value)}
            placeholder="Your Name"
            style={{ width: '100%', padding: '10px', fontSize: '1em', backgroundColor: '#4a4a4a', color: '#fff', border: '1px solid #666', marginBottom: '10px' }}
          />
          <textarea
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.target.value)}
            placeholder="Enter your notes here..."
            style={{ width: '100%', height: '150px', padding: '10px', fontSize: '1em', backgroundColor: '#4a4a4a', color: '#fff', border: '1px solid #666' }}
          />
          <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
            <button onClick={handleSendFeedback}>Send</button>
            <button onClick={() => setIsFeedbackOpen(false)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default FormulaLibrary;